import { fetchAgentStatus } from "@/lib/order-assistant/client";
import sdk from "@repo/api-sdk";
import { queryOptions, useQuery } from "@tanstack/react-query";

export const orderAssistantQueryKeys = {
  all: ["order-assistant"] as const,
  /** Per customer: the authenticated status answer depends on who is signed in. */
  status: (customerId: string) =>
    [...orderAssistantQueryKeys.all, "status", customerId] as const,
  supplierProduct: (id: string) =>
    [...orderAssistantQueryKeys.all, "supplier-product", id] as const
};

/**
 * `GET /store/agent/status`. A failed ask (an api without the plugin, a
 * network error) reads as "not available": the entry button only appears for an
 * explicit `enabled: true`.
 */
export function orderAssistantStatusQuery(customerId: string) {
  return queryOptions({
    queryKey: orderAssistantQueryKeys.status(customerId),
    queryFn: ({ signal }) => fetchAgentStatus(signal),
    retry: false,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false
  });
}

export function useOrderAssistantEnabled(customerId: string): boolean {
  const { data } = useQuery(orderAssistantStatusQuery(customerId));
  return data?.enabled === true;
}

/** A supplier product's public detail for the drawer's read-only view. */
export function useSupplierProduct(id: string | null) {
  return useQuery({
    queryKey: orderAssistantQueryKeys.supplierProduct(id ?? ""),
    queryFn: ({ signal }) => sdk.store.supplierCatalog.product(id!, { signal }),
    enabled: id !== null,
    retry: false,
    staleTime: 60 * 1000
  });
}
